import { useRef } from 'react'
import type { ChangeEvent, FocusEvent, FormEvent, Ref } from 'react'
import { useHistory } from 'react-router'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'

import SearchInput from 'components/misc/SearchInput'
import SearchPills from 'components/search/SearchPills'
import Dropdown from 'components/search/Dropdown'
import { useSelector } from 'store/hooks'
import { selectMaybeDoi } from 'store/ui/selectors'
import { selectSearchPills } from 'store/search/selectors'
import { setSearchTerm, fetchAutocompletes, flushAutocompletes, setSearchFocused } from 'store/ui/actions'
import { Fade } from 'components/misc/Fade'
import styles from './styles/SearchBox.css'

interface SearchBoxProps {
  className?: string
  inputClass?: string
  buttonClass?: string
  buttonIconClass?: string
  dropdownClass?: string
  placeholder?: string
  dropdown?: boolean
  // Called without an event when an autocomplete result is picked.
  onSubmit?: (e?: FormEvent) => void
  inputRef?: Ref<HTMLInputElement>
}

export const SearchBox = ({
  className,
  inputClass,
  buttonClass,
  buttonIconClass,
  dropdownClass,
  placeholder,
  dropdown,
  onSubmit,
  inputRef
}: SearchBoxProps) => {
  const ref = useRef<HTMLFormElement>(null)
  const history = useHistory()
  const dispatch = useDispatch()
  const term = useSelector(({ ui }) => ui.search.term)
  const focused = useSelector(({ ui }) => ui.search.focused)
  const maybeDoi = useSelector(({ ui }) => selectMaybeDoi(ui))
  const searchPills = useSelector(({ search }) => selectSearchPills(search))
  const hasSearchPills = !!searchPills.length

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value.trimLeft()
    dispatch(setSearchTerm(newTerm))

    if (newTerm.length > 2) {
      dispatch(fetchAutocompletes(newTerm, 'title'))
    }
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (e && e.target.dataset.showdropdown && e.target.type !== 'text') {
      return
    }
    dispatch(setSearchFocused(false))
  }

  const handleFocus = () => {
    dispatch(setSearchFocused(true))
  }

  const handleClick = () => dispatch(setSearchFocused(true))

  const handleSubmit = (e?: FormEvent) => {
    e && e.preventDefault()
    ;(document.activeElement as HTMLElement).blur()

    dispatch(setSearchFocused(false))

    if (maybeDoi) {
      return history.push(`/reports/${maybeDoi.trim()}`)
    }

    onSubmit && onSubmit(e)
  }

  const handleDropdownClick = (val: string) => {
    dispatch(setSearchTerm(val))
    // Completions are cached under 'title', so flushing 'term' leaves them in place.
    dispatch(flushAutocompletes('term'))
    handleSubmit()
  }

  return (
    <SearchInput
      ref={ref}
      className={classNames(className, styles.input, {
        [styles.focused]: focused
      })}
      inputClass={inputClass}
      buttonClass={buttonClass}
      buttonIconClass={buttonIconClass}
      value={term}
      placeholder={hasSearchPills ? '' : placeholder}
      onSubmit={handleSubmit}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleSearchChange}
      inputRef={inputRef}
    >
      <SearchPills />
      <Fade className={styles.dropdowns} show={focused}>
        {dropdown && (
          <Dropdown
            dropdownStyle={dropdownClass}
            field='title'
            term={term}
            onClickResult={handleDropdownClick}
            showLinks
          />
        )}
      </Fade>
    </SearchInput>
  )
}

export default SearchBox
